import { log } from '../app/logger.js';
import { window } from '../window.js';
import { PopupListControl } from './popup_list_control.js';

/**
 * Autocomplete control represents a text field with a popup of items which
 * contains a list of suggestions.
 */
class AutocompleteControl extends PopupListControl {
  constructor() {
    super();
    this.oninput = null;
  }

  initialize() {
    super.initialize();

    this.classList.add('autocomplete');
    this.boxElement.classList.add('autocomplete-box');

    this.inputElement.addEventListener(
      'keydown',
      ev => {
        log(`[control:autocomplete:event] keydown: ${ev.key}`);
        this.onKeyDown(ev);
      },
      false
    );
    this.inputElement.addEventListener('click', this.onClick.bind(this), false);
    this.inputElement.addEventListener('focus', this.onFocus.bind(this), false);
    this.inputElement.addEventListener('blur', ev => {
      this.cancelItemsUpdate();
      this.onBlur(ev);
    });

    // Update items on user input.
    this.inputElement.addEventListener('input', ev => {
      log(
        `[control:autocomplete:event] oninput on ${ev.target.nodeName}, value: ${ev.target.value}`
      );
      this.scheduleItemsUpdate();
      this.open = true;
      if (this.oninput) {
        this.oninput(this.value);
      }
    });
  }

  static properties = {
    readOnly: {
      set(v) {
        this.inputElement.readOnly = v;
      },
    },
    placeholder: {
      attribute: true,
      set(v) {
        this.inputElement.placeholder = v;
      },
    },
  };

  static get boxMarkup() {
    return `
  <div class='autocomplete-buttons-container'></div>
  <input class='autocomplete-input'>
  <div class='autocomplete-buttons-container'></div>
`;
  }

  get value() {
    return this.inputElement.value;
  }
  set value(val) {
    log(`[control:autocomplete] set value to ${val}`);
    this.inputElement.value = val;
  }

  get onfocus() {
    return this._onfocus;
  }
  set onfocus(callback) {
    this._onfocus = callback;
  }
  get onblur() {
    return this._onblur;
  }
  set onblur(callback) {
    this._onblur = callback;
  }

  focus() {
    this.inputElement.focus();
  }

  get open() {
    return super.open;
  }
  set open(val) {
    log(
      `[control:autocomplete] schedule autocomplete ${val ? `open` : `close`}`
    );
    this.lastOpenVal = val;
    if (this.openPromise) {
      return;
    }

    // Wait for items update before opening a popup.
    this.openPromise = Promise.resolve(val && this.itemsUpdatePromise).then(
      () => {
        log(
          `[control:autocomplete] autocomplete is ${
            this.lastOpenVal ? `opened` : `closed`
          }`
        );
        super.open = this.lastOpenVal;
        this.openPromise = null;
      }
    );
  }

  scheduleItemsUpdate() {
    const scheduleItemsUpdateHash = Math.random();
    this.scheduleItemsUpdateHash = scheduleItemsUpdateHash;

    if (!this.itemsUpdatePromise) {
      log(
        `[control:autocomplete] scheduleItemsUpdate: create itemsUpdatePromise`
      );
      this.itemsUpdatePromise = new Promise(
        resolve => (this.itemsUpdatePromiseResolve = resolve)
      );
    }

    if (this.updateItemsTimeout) {
      clearTimeout(this.updateItemsTimeout);
    }

    this.updateItemsTimeout = setTimeout(() => this.updateItems(), 100);

    return this.itemsUpdatePromise.then(() => scheduleItemsUpdateHash);
  }

  cancelItemsUpdate() {
    if (this.updateItemsTimeout) {
      clearTimeout(this.updateItemsTimeout);
      this.updateItemsTimeout = 0;
    }
    if (this.itemsUpdatePromise) {
      log(
        `[control:autocomplete] cancelItemsUpdate: resolve itemsUpdatePromise`
      );
      this.itemsUpdatePromiseResolve();
      this.itemsUpdatePromise = null;
      this.itemsUpdatePromiseResolve = null;
    }
  }

  async updateItems() {
    this.updateItemsTimeout = 0;

    // Schedule re-search.
    if (this.searching) {
      this.scheduleItemsUpdate();
      return;
    }

    // Start the items requests.
    this.searching = true;
    log(`[control:autocomplete] updateItems: requestItems start`);
    let items = await ((this.requestItems &&
      this.requestItems(this.inputElement.value)) ||
      []);
    log(`[control:autocomplete] updateItems: requestItems end`);
    this.searching = false;

    // No UI if re-search is scheduled or update was cancelled.
    if (this.updateItemsTimeout || !this.itemsUpdatePromise) {
      if (this.updateItemsTimeout) {
        log(`[control:autocomplete] updateItems: new updateItems is pending`);
      }
      if (!this.itemsUpdatePromise) {
        log(`[control:autocomplete] updateItems: was cancelled`);
      }
      return;
    }

    // If items were changed (compare by name) then update UI.
    if (
      !this.itemsData ||
      this.itemsData.length != items.length ||
      this.itemsData.some((e, i) =>
        Array.from(Object.keys(e)).some(prop => e[prop] != items[i].prop)
      )
    ) {
      this.setItemsNSelect(items);
    }

    log(`[control:autocomplete] updateItems: resolve itemsUpdatePromise`);
    this.itemsUpdatePromiseResolve();
    this.itemsUpdatePromise = null;
    this.itemsUpdatePromiseResolve = null;
  }

  setItemsNSelect(items) {
    this.setItems(items);
    this.selectedIndex = 0;
  }

  onFocus(ev) {
    // Focus reenty, ignore it. Happens, when clicking a list item.
    if (this.open) {
      return;
    }

    // Attempt to get a default list when no value.
    if (ev.target.value == '') {
      this.scheduleItemsUpdate();
    }
    if (this._onfocus) {
      this._onfocus();
    }
  }

  onClick() {
    if (!this.readOnly) {
      this.open = !this.open;
    }
  }

  onKeyDown(ev) {
    super.onKeyDown(ev);
    if (ev.defaultPrevented) {
      return;
    }

    switch (ev.key) {
      case 'ArrowDown':
        if (
          this.inputElement.selectionStart == this.inputElement.value.length
        ) {
          this.selected = null;
          this.open = true;
        }
        break;

      case 'ArrowUp':
        if (this.inputElement.selectionEnd == 0) {
          this.selected = null;
          this.open = true;
        }
        break;

      default:
        break;
    }
  }

  static get selectors() {
    return Object.assign(
      {
        Input: '.autocomplete-input',
        LeftButtons: '.autocomplete-buttons-container:first-child',
        RightButtons: '.autocomplete-buttons-container:last-child',
      },
      super.selectors
    );
  }

  static get elements() {
    let s = this.selectors;
    return {
      inputElement: s.Input,
      leftButtonsElement: s.LeftButtons,
      rightButtonsElement: s.RightButtons,
    };
  }

  isInputElement(el) {
    return el == this.inputElement;
  }
}

window.customElements.define('autocomplete-control', AutocompleteControl);

export { AutocompleteControl };
